import { useState, useEffect } from "react";
import { Cpu, Globe, Users, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ORIENT_COLORS = { care: "#34d399", utility: "#60a5fa", creative: "#a78bfa", extraction: "#f87171" };
const ORIENT_LABELS = { care: "Care", utility: "Utility", creative: "Creative", extraction: "Extraction" };
const FORM_COLORS = { wearable: "#34d399", robotics: "#60a5fa", hardware: "#a78bfa", embedded: "#f59e0b", hybrid: "#06b6d4" };
const PIE_COLORS = ["#34d399", "#60a5fa", "#a78bfa", "#f87171", "#f59e0b", "#06b6d4"];

export const PhysicalAIAnatomy = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOrient, setActiveOrient] = useState(null);

  useEffect(() => {
    axios.get(`${API}/anatomy/devices/overview`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <Cpu className="w-10 h-10 text-blue-400/40 mx-auto mb-3 animate-pulse" />
        <p className="text-sm text-slate-400">Scanning physical AI landscape...</p>
      </div>
    </div>
  );
  if (!data) return null;

  const orientData = Object.entries(data.orientation || {}).map(([k, v]) => ({ name: ORIENT_LABELS[k] || k, value: v, color: ORIENT_COLORS[k] }));
  const formData = Object.entries(data.form_factors || {}).map(([k, v]) => ({ name: k, value: v }));
  const founderData = Object.entries(data.founders || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="physical-ai-anatomy">
      {/* Hero */}
      <div className="rounded-2xl py-8 px-6 text-center" style={{ background: "linear-gradient(135deg, rgba(96,165,250,0.06) 0%, rgba(96,165,250,0.02) 100%)", border: "1px solid rgba(96,165,250,0.15)" }}>
        <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(96,165,250,0.15)" }}>
          <Cpu className="w-7 h-7 text-blue-400" />
        </div>
        <h2 className="text-2xl font-bold mb-1">The Embodied</h2>
        <p className="text-xs font-bold text-blue-400 uppercase tracking-[0.2em] mb-2">Physical AI Observatory</p>
        <p className="text-sm text-slate-400 max-w-lg mx-auto">
          {data.total} AI devices and hardware across {data.countries_count || data.countries?.length || 0} countries.
          When AI leaves the screen and enters the physical world, what values does it carry?
        </p>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{data.total}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Devices Tracked</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400">{data.comparison?.hw_care_pct}%</p>
          <p className="text-[10px] text-muted-foreground mt-1">Care-Oriented</p>
          <p className="text-[10px] text-muted-foreground/60">vs {data.comparison?.sw_care_pct}% in software</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-violet-400">{data.countries_count || data.countries?.length || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Countries</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-pink-400">{data.comparison?.female_pct}%</p>
          <p className="text-[10px] text-muted-foreground mt-1">Female-Founded</p>
        </div>
      </div>

      {/* Key insight */}
      <div className="bg-emerald-500/[0.04] border border-emerald-500/15 rounded-xl p-4 text-center">
        <p className="text-sm text-slate-300">
          Physical AI is <span className="text-emerald-400 font-bold">{data.comparison?.hw_care_pct}% care-oriented</span> vs software at <span className="text-blue-400 font-bold">{data.comparison?.sw_care_pct}%</span>. When we build things people can touch, we build with more empathy.
        </p>
      </div>

      {/* Orientation + Form Factor charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Orientation pie */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Value Orientation</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={orientData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                  labelLine={false} fontSize={10}>
                  {orientData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} formatter={(v, name) => [`${v} devices`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {orientData.map((e, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />{e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>

        {/* Form factor bar */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Form Factors</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={formData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={70} stroke="rgba(255,255,255,0.1)" />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} animationDuration={1000}>
                  {formData.map((e, i) => <Cell key={i} fill={FORM_COLORS[e.name] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Countries + Founders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Where hardware AI is built</h3>
          <div className="space-y-2">
            {data.countries?.slice(0, 10).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-20 truncate">{c.country}</span>
                <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-blue-400" style={{ width: `${(c.count / (data.countries[0]?.count || 1)) * 100}%`, opacity: 1 - i * 0.08 }} />
                </div>
                <span className="text-xs font-bold text-muted-foreground w-8 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Who builds physical AI</h3>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={founderData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={70} stroke="rgba(255,255,255,0.1)" />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="value" fill="#60a5fa" radius={[0, 4, 4, 0]} animationDuration={1000} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Devices by orientation — expandable sections */}
      <div>
        <h3 className="text-sm font-bold mb-3">Devices by Orientation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["care", "utility", "creative", "extraction"].map(o => {
            const devices = data.devices_by_orientation?.[o] || [];
            const isActive = activeOrient === o;
            if (devices.length === 0) return null;
            return (
              <div key={o} onClick={() => setActiveOrient(isActive ? null : o)}
                className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${isActive ? "border-white/[0.15] bg-white/[0.04]" : "border-border hover:border-white/[0.1]"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: ORIENT_COLORS[o] }} />
                    <span className="text-sm font-semibold" style={{ color: ORIENT_COLORS[o] }}>{ORIENT_LABELS[o]}</span>
                    <span className="text-xs text-muted-foreground">({devices.length})</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isActive ? "rotate-90" : ""}`} />
                </div>
                {isActive && (
                  <div className="space-y-1.5 pt-2 border-t border-white/[0.06]">
                    {devices.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 py-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md border border-white/[0.08] text-slate-400">{d.form_factor}</span>
                        <span className="text-xs font-medium text-slate-300 truncate">{d.product_name}</span>
                        {d.country && <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">{d.country}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Notable devices */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-bold mb-3">Notable Physical AI Devices</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.notable_devices?.slice(0, 16).map((d, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/[0.03]">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ORIENT_COLORS[d.value_orientation] || "#64748b" }} />
              <span className="text-xs font-medium text-slate-300 truncate flex-1">{d.product_name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-md border border-white/[0.06] text-slate-500">{d.form_factor}</span>
              {d.country && <span className="text-[10px] text-muted-foreground">{d.country}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Provocative question */}
      <div className="text-center py-6 rounded-2xl" style={{ background: "rgba(96,165,250,0.04)", border: "1px solid rgba(96,165,250,0.12)" }}>
        <p className="text-lg font-bold text-white max-w-lg mx-auto italic">"When technology extends the human form, inclusion is not optional."</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <span>{typeof window !== "undefined" ? window.location.origin.replace("https://", "") : ""}</span>
        <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
      </div>
    </div>
  );
};

export default PhysicalAIAnatomy;
